import { Pie } from "@ant-design/plots";
import { PieChart } from "lucide-react";
import { Registration } from "@/utils/types/NPS/Stepper/GetRegistrationDetils";

const AssetAllocation: React.FC<{
  registrationDetails: Registration | null;
}> = ({ registrationDetails }) => {
  if (!registrationDetails) {
    return <div>Loading...</div>;
  }

  // Extract choice type
  const choice = registrationDetails?.schemeDtls?.[0]?.schPrefChoice;

  // -------------------------------
  // STATIC PRESET ALLOCATIONS
  // -------------------------------
  const presetSchemes: any = {
    B: [
      { schemeName: "Equity", schemePercentage: "50%" },
      { schemeName: "Corporate", schemePercentage: "30%" },
      { schemeName: "Government", schemePercentage: "20%" },
    ],
    H: [
      { schemeName: "Equity", schemePercentage: "75%" },
      { schemeName: "Corporate", schemePercentage: "10%" },
      { schemeName: "Government", schemePercentage: "15%" },
    ],
    L: [
      { schemeName: "Equity", schemePercentage: "25%" },
      { schemeName: "Corporate", schemePercentage: "45%" },
      { schemeName: "Government", schemePercentage: "30%" },
    ],
    A: [
      { schemeName: "Equity", schemePercentage: "50%" },
      { schemeName: "Corporate", schemePercentage: "30%" },
      { schemeName: "Government", schemePercentage: "20%" },
    ],
  };

  // -------------------------------
  // BUILD FINAL ALLOCATION DATA
  // -------------------------------
  let allocation: { name: string; value: number }[] = [];

  if (choice === "V") {
    allocation =
      registrationDetails.schemeDtls?.[0]?.schemeList?.map((s: any) => ({
        name: s.schemeName,
        value: Number(s.schemePercentage),
      })) ?? [];
  } else {
    // Use static preset mapping
    const selected = presetSchemes[choice] ?? [];
    allocation = selected.map((s: any) => ({
      name: s.schemeName,
      value: Number(s.schemePercentage.replace("%", "")),
    }));
  }

  // -------------------------------
  // PIE CONFIG
  // -------------------------------
  const pieConfig = {
    data: allocation,
    angleField: "value",
    colorField: "name",
    radius: 1,
    innerRadius: 0.7,
    legend: false,
    label: false,
    color: ["#3B82F6", "#22C55E", "#F59E0B", "#8B5CF6"],
    interactions: [{ type: "element-active" }],

    statistic: {
      title: { content: "Total" },
      content: {
        content: `${allocation.reduce((a, b) => a + b.value, 0)}%`,
        style: {
          fontSize: "18px",
          fontWeight: 700,
          fill: "hsl(var(--secondary))",
        },
      },
    },
  };

  return (
    <div className="rounded-xl border bg-card shadow-sm">
      <div className="flex items-center justify-between border-b p-4">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-secondary">
          <PieChart className="h-5 w-5 text-primary" />
          Asset Allocation
        </h2>

        <button className="rounded-lg border px-3 py-1.5 text-sm transition hover:bg-muted">
          Change Allocation
        </button>
      </div>

      {/* BODY */}
      <div className="flex flex-col items-center gap-6 p-4 md:flex-row">
        {/* PIE */}
        <div className="h-[200px] w-[200px]">
          <Pie {...pieConfig} />
        </div>

        {/* RIGHT SIDE BOXES */}
        <div className="grid flex-1 grid-cols-2 gap-4">
          {allocation.map((item, index) => (
            <div key={item.name} className="rounded-lg bg-muted/30 p-3">
              <div className="mb-2 flex items-center gap-2">
                <div
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: pieConfig.color[index] }}
                />
                <span className="text-sm text-muted-foreground">
                  {item.name}
                </span>
              </div>

              <p className="text-xl font-bold text-secondary">{item.value}%</p>
              <p className="text-xs text-muted-foreground">Auto Calculated</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AssetAllocation;
